import { getRandomColor, Switch, Tooltip } from '@rocketlane/ui';
import { formatTime } from '@vidstack/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useVideoPlayerContext } from './player-context';
import { Transcript, TranscriptSegment } from './types';

type SpeakerTimelineProps = {
  name: string;
  segments: TranscriptSegment[];
  activeSpeaker?: string;
  onSwitchToSpeakerOnly?: (speaker?: string) => void;
};
export const SpeakerTimeline = ({
  name,
  segments,
  activeSpeaker,
  onSwitchToSpeakerOnly,
}: SpeakerTimelineProps) => {
  const color = useMemo(() => getRandomColor(name), [name]);
  const { duration, moveToTime, canPlay, currentTime } =
    useVideoPlayerContext();

  const totalDuration = useMemo(() => {
    if (duration > 0) {
      return duration;
    }

    const maxEndTime = Math.max(...segments.map((s) => s.endTime), 0);

    return maxEndTime;
  }, [duration, segments]);
  const segmentsWithinDuration = useMemo(() => {
    return segments.filter((segment) => segment.endTime < totalDuration);
  }, [segments, totalDuration]);
  const seekBarRef = useRef<HTMLDivElement>(null);
  const [leftPercent, setLeftPercent] = useState<number | null>(null);
  const [pointerTime, setPointerTime] = useState<number | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragTime, setDragTime] = useState<number | null>(null);

  const calculateTimeFromClientX = useCallback(
    (clientX: number) => {
      const { left, width } = seekBarRef.current?.getBoundingClientRect() || {
        left: 0,
        width: 0,
      };
      const percent = Math.max(0, Math.min(1, (clientX - left) / width));

      return percent * totalDuration;
    },
    [totalDuration],
  );

  const calculateLeftPercent = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (!canPlay || isDragging) {
        return;
      }

      const time = calculateTimeFromClientX(event.clientX);
      const percent = time / totalDuration;

      setLeftPercent(percent);
      setPointerTime(time);
    },
    [totalDuration, canPlay, isDragging, calculateTimeFromClientX],
  );

  const onSeekBarMouseLeave = useCallback(() => {
    if (!isDragging) {
      setPointerTime(null);
      setLeftPercent(null);
    }
  }, [isDragging]);

  const handleSeekBarMouseDown = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (!canPlay) {
        return;
      }
      event.preventDefault();
      setIsDragging(true);
      const time = calculateTimeFromClientX(event.clientX);

      setDragTime(time);
      const percent = time / totalDuration;

      setLeftPercent(percent);
      setPointerTime(time);
    },
    [canPlay, calculateTimeFromClientX, totalDuration],
  );

  const handleSeekBarTouchStart = useCallback(
    (event: React.TouchEvent<HTMLDivElement>) => {
      if (!canPlay) {
        return;
      }
      event.preventDefault();
      const touch = event.touches[0];

      if (touch) {
        setIsDragging(true);
        const time = calculateTimeFromClientX(touch.clientX);

        setDragTime(time);
        const percent = time / totalDuration;

        setLeftPercent(percent);
        setPointerTime(time);
      }
    },
    [canPlay, calculateTimeFromClientX, totalDuration],
  );

  useEffect(() => {
    if (!isDragging) {
      return;
    }

    const handleMouseMove = (event: MouseEvent) => {
      const time = calculateTimeFromClientX(event.clientX);

      setDragTime(time);
      const percent = time / totalDuration;

      setLeftPercent(percent);
      setPointerTime(time);
    };

    const handleTouchMove = (event: TouchEvent) => {
      const touch = event.touches[0];

      if (touch) {
        const time = calculateTimeFromClientX(touch.clientX);

        setDragTime(time);
        const percent = time / totalDuration;

        setLeftPercent(percent);
        setPointerTime(time);
      }
    };

    const handleMouseUp = () => {
      if (dragTime != null) {
        moveToTime?.(dragTime);
      }
      setIsDragging(false);
      setDragTime(null);
    };

    const handleTouchEnd = () => {
      if (dragTime != null) {
        moveToTime?.(dragTime);
      }
      setIsDragging(false);
      setDragTime(null);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    document.addEventListener('touchend', handleTouchEnd);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [
    isDragging,
    dragTime,
    calculateTimeFromClientX,
    totalDuration,
    moveToTime,
  ]);

  const seekBarThumbLeftPercent = useMemo(() => {
    if (isDragging && dragTime != null) {
      return dragTime / totalDuration;
    }

    return currentTime / duration;
  }, [currentTime, duration, isDragging, dragTime, totalDuration]);
  const isActiveSpeaker = activeSpeaker === name;
  const handleSwitchToSpeakerOnly = useCallback(
    (checked: boolean) => {
      onSwitchToSpeakerOnly?.(checked ? name : undefined);
    },
    [name, onSwitchToSpeakerOnly],
  );

  return (
    <div className="flex flex-col gap-02 py-03 group/speaker-timeline">
      <div
        className={`flex items-center group-hover/speaker-timeline:gap-[var(--spacing-02)] ${isActiveSpeaker ? 'gap-02' : ''}`}
      >
        <Tooltip
          content={
            isActiveSpeaker ? (
              <FormattedMessage id="account.meeting_transcript.speaker_timeline.stop_listening" />
            ) : (
              <FormattedMessage id="account.meeting_transcript.speaker_timeline.listen_to_speaker_only" />
            )
          }
        >
          <div
            className={`transition-width duration-300 overflow-hidden ${isActiveSpeaker ? 'w-[24px]' : 'w-0 group-hover/speaker-timeline:w-[24px]'}`}
          >
            <Switch
              size="small"
              className="mt-[1px] p-0"
              checked={isActiveSpeaker}
              onCheckedChange={handleSwitchToSpeakerOnly}
            />
          </div>
        </Tooltip>
        <div className="text-text-primary font-[var(--font-weight-bold)] leading-[var(--line-height-18)] text-[12px]">
          {name}
        </div>
      </div>
      <div
        className={`h-[8px] bg-[var(--coolest-gray-20)] w-full relative rounded-8 group/speakerseekbar touch-none select-none ${canPlay ? 'cursor-pointer' : 'cursor-default'}`}
        ref={seekBarRef}
        onMouseMove={calculateLeftPercent}
        onMouseLeave={onSeekBarMouseLeave}
        onMouseDown={handleSeekBarMouseDown}
        onTouchStart={handleSeekBarTouchStart}
      >
        {segmentsWithinDuration.map((segment, index) => {
          const leftPercent = (segment.startTime / totalDuration) * 100;
          const widthPercent =
            ((segment.endTime - segment.startTime) / totalDuration) * 100;

          return (
            <div
              key={`${segment.startTime}-${segment.endTime}-${index}`}
              className="absolute h-full rounded-8"
              style={{
                left: `${leftPercent}%`,
                width: `${widthPercent}%`,
                backgroundColor: `var(--${color}-40)`,
              }}
            />
          );
        })}
        {(isDragging || pointerTime) && (
          <div
            className="absolute bottom-[12px] rounded-4 bg-black p-02 label-01 text-text-on-color shadow-32 -translate-x-1/2"
            style={{
              left: `${(leftPercent ?? seekBarThumbLeftPercent) * 100}%`,
            }}
          >
            {formatTime(pointerTime ?? dragTime ?? currentTime)}
          </div>
        )}
        <div
          className={`absolute top-1/2 h-[10px] w-[12px] -translate-y-1/2 rounded-8 bg-white border border-solid border-[var(--blue-40)] transition-[height,width,opacity] duration-300 ${isDragging ? 'opacity-100' : 'opacity-0 group-hover/speakerseekbar:opacity-100'}`}
          style={{
            left: `max(0%, min(calc(${seekBarThumbLeftPercent * 100}% - 6px), calc(100% - 12px)))`,
          }}
        />
      </div>
    </div>
  );
};

type SpeakersTimelineProps = {
  transcript: Transcript;
};
export const SpeakersTimeline = ({ transcript }: SpeakersTimelineProps) => {
  const transcriptGroupedBySpeaker = useMemo(() => {
    return groupedTranscriptToSpeakerTimeline(transcript);
  }, [transcript]);
  const { currentTime, duration, canPlay, moveToTime } =
    useVideoPlayerContext();
  const leftPercent = useMemo(() => {
    return (currentTime / duration) * 100;
  }, [currentTime, duration]);
  const [listenToSpeaker, setListenToSpeaker] = useState<string>();
  const switchToSpeakerOnly = useCallback((speaker?: string) => {
    setListenToSpeaker(speaker);
  }, []);

  const speakerSegments = useMemo(() => {
    if (!listenToSpeaker) {
      return [];
    }
    const speaker = transcriptGroupedBySpeaker.find(
      (s) => s.name === listenToSpeaker,
    );

    return speaker?.segments ?? [];
  }, [listenToSpeaker, transcriptGroupedBySpeaker]);

  useEffect(() => {
    if (!listenToSpeaker || !canPlay || speakerSegments.length === 0) {
      return;
    }

    const isWithinSegment = speakerSegments.some(
      (segment) =>
        currentTime >= segment.startTime && currentTime < segment.endTime,
    );

    if (!isWithinSegment) {
      const nextSegment = speakerSegments.find(
        (segment) => segment.startTime > currentTime,
      );

      if (nextSegment) {
        moveToTime(nextSegment.startTime, false);
      } else {
        moveToTime(duration, false);
      }
    }
  }, [
    listenToSpeaker,
    currentTime,
    canPlay,
    speakerSegments,
    moveToTime,
    duration,
  ]);

  return (
    <div className="flex flex-col gap-03 relative">
      {transcriptGroupedBySpeaker.map((speaker) => (
        <SpeakerTimeline
          key={speaker.name}
          name={speaker.name}
          segments={speaker.segments}
          activeSpeaker={listenToSpeaker}
          onSwitchToSpeakerOnly={switchToSpeakerOnly}
        />
      ))}
      {canPlay && (
        <div
          className="absolute bg-[var(--blue-40)] w-[1px] top-[30px] bottom-[8px]"
          style={{
            left: `max(0%, min(calc(${leftPercent}% - .5px), calc(100% - 1px)))`,
          }}
        />
      )}
    </div>
  );
};

export const groupedTranscriptToSpeakerTimeline = (
  transcript: Transcript,
): { name: string; segments: TranscriptSegment[] }[] => {
  const groupedTranscript = transcript.segments.reduce((acc, segment) => {
    acc[segment.speaker] = (acc[segment.speaker] || []).concat(segment);

    return acc;
  }, {});

  return Object.keys(groupedTranscript).map((speaker) => {
    return {
      name: speaker,
      segments: groupedTranscript[speaker] || [],
    };
  });
};
